import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";

/** The acknowledgement is an EVENT on the PO. Paste the vendor's reply and the dates are read
 *  deterministically; anything the parser cannot see is asked for, never guessed. */
export default function AckDialog({ project, po, onClose, onDone }) {
  const [text, setText] = useState("");
  const [fields, setFields] = useState({ order_date: "", ack_date: "", est_ship_date: "",
    vendor_order_number: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const save = async () => {
    if (!text.trim() && !fields.ack_date) {
      toast.error("Paste the acknowledgement, or type the dates");
      return;
    }
    setBusy(true);
    try {
      const res = await opsApi.poAck(project.id, po.id, { text, ...fields, source: "paste" });
      if (res.duplicate) {
        toast.info(res.message);
        onDone();
        return;
      }
      setResult(res.ack);
      toast.success(`Acknowledgement filed against ${po.po_number}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be filed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
         onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-po-ack-dialog">
        <h3 className="text-base font-semibold text-slate-900">
          Acknowledgement · {po.po_number}
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          Paste the vendor's confirmation. The order date, acknowledgement date, estimated ship
          date, lead time and order number are read from it; the raw text is kept on the record.
        </p>

        <Textarea rows={7} value={text} onChange={(e) => setText(e.target.value)}
                  className="mt-3 text-xs" data-testid="ops-po-ack-text"
                  placeholder={"Order date: 03/02/2026\nEstimated ship date: 04/15/2026\nSales Order # SO-99812"} />

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {[["ack_date", "Acknowledged", "date"], ["est_ship_date", "Estimated ship", "date"],
            ["order_date", "Order date", "date"],
            ["vendor_order_number", "Vendor order #", "text"]].map(([k, label, type]) => (
            <label key={k} className="block text-xs">
              <span className="text-slate-500">{label}</span>
              <Input type={type} value={fields[k]}
                     onChange={(e) => setFields((f) => ({ ...f, [k]: e.target.value }))}
                     className="mt-1 h-8 text-xs" data-testid={`ops-po-ack-${k}`} />
            </label>
          ))}
        </div>

        {result && (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs"
               data-testid="ops-po-ack-result">
            <p className="font-semibold text-slate-800">
              Read {result.method} · confidence {Number(result.confidence).toFixed(2)}
              {result.needs_review ? " · needs review" : ""}
            </p>
            <p className="mt-0.5 text-slate-600">
              Acknowledged {result.ack_date || "—"} · ships {result.est_ship_date || "—"} ·
              order {result.vendor_order_number || "—"}
            </p>
            {result.missing?.length > 0 && (
              <p className="mt-1 text-amber-700">
                Could not read: {result.missing.join(", ")} — type them above and file again.
              </p>
            )}
            {result.discrepancies?.length > 0 && (
              <ul className="mt-1 space-y-0.5 text-rose-700">
                {result.discrepancies.map((d, i) => <li key={i}>{d.message}</li>)}
              </ul>
            )}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={result ? onDone : onClose}
                  data-testid="ops-po-ack-close">{result ? "Done" : "Cancel"}</Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="ops-po-ack-save">
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} File the acknowledgement
          </Button>
        </div>
      </div>
    </div>
  );
}
